<template>
  <div>
    <h3 class="pt-3">{{ $t("followup.title") }}</h3>
    <h5 v-if="monitorings.length === 0">{{ $t("followup.instruction") }}</h5>
    <b-row align-h="center">
      <b-col v-if="!hasResponse" cols="8" lg="4" md="6">
        <b-card no-body>
          <b-card-body>
            <b-form @submit="checkProtocol">
              <b-form-group :label="$t('labels.protocol')">
                <b-form-input
                  v-model="protocolModel"
                  v-validate="rules.protocol"
                  class="form-control"
                  aria-describedby="invalidProtocol"
                  name="protocol"
                  :state="validateState('protocol')"
                  @input="protocolInfo = null"
                />
                <b-form-invalid-feedback id="invalidProtocol">
                  {{ errors.first("protocol") }}
                </b-form-invalid-feedback>
              </b-form-group>
            </b-form>
            <b-alert :show="protocolInfo !== null">{{ protocolInfo }}</b-alert>
            <b-button
              :disabled="loading"
              class="action-button"
              @click="$router.go(-1)"
              ><i class="fas fa-home" aria-hidden="true" />
              {{ $t("labels.home") }}
            </b-button>
            <b-button
              :disabled="loading"
              class="action-button"
              variant="primary"
              @click="checkProtocol"
              ><i class="fas fa-search" aria-hidden="true" />
              {{ $t("labels.search") }}
            </b-button>
          </b-card-body>
        </b-card>
      </b-col>
      <b-col v-else cols="8">
        <b-card no-header>
          <template slot="header">
            {{ $t("followup.resultTitle") }} <strong>{{ protocol }}</strong>
            <br />
            <em v-if="createdAt">
              {{ $t("followup.receivedDay") }} {{ createdAt | date }}
              {{ $t("followup.receivedTime") }} {{ createdAt | time }}
            </em>
          </template>
          <b-card-body>
            <template v-if="type === ProtocolType.COMPLAINT">
              <b-alert :show="protocolInfo !== null">{{
                protocolInfo
              }}</b-alert>
              <b-card v-for="monitoring in monitorings" :key="monitoring._id">
                <p class="card-text">
                  <i class="fa fa-calendar-alt" aria-hidden="true" />
                  {{ monitoring.createdAt | datetime }}
                </p>
                <p
                  v-if="monitoring.type === MonitoringType.COMPANY"
                  class="card-text"
                >
                  {{ monitoring.description }}
                </p>
                <p
                  v-else-if="monitoring.type === MonitoringType.RESPONSE"
                  class="card-text text-info"
                >
                  <i class="fas fa-reply" aria-hidden="true" />
                  {{ monitoring.response.text }}
                </p>
              </b-card>
              <template v-if="response.enabled">
                <b-row>
                  <b-col>
                    <b-form-group
                      class="mb-0"
                      :label="$t('labels.response')"
                      :description="$t('labels.responseDesc')"
                    >
                      <b-form-textarea
                        v-model="response.text"
                        v-validate="rules.response"
                        class="form-control"
                        aria-describedby="invalidResponse"
                        name="response"
                        :state="validateState('response')"
                      />
                      <b-form-invalid-feedback id="invalidResponse">
                        {{ errors.first("response") }}
                      </b-form-invalid-feedback>
                    </b-form-group>
                  </b-col>
                </b-row>
                <b-row>
                  <b-col>
                    <b-button
                      variant="link"
                      :disabled="loading"
                      class="float-right"
                      @click="sendResponse"
                    >
                      <i class="fas fa-reply" aria-hidden="true" />
                      {{ $t("labels.sendResponse") }}
                    </b-button>
                  </b-col>
                </b-row>
                <hr />
              </template>
            </template>
            <template v-if="type === ProtocolType.DSAR">
              <b-alert :show="protocolInfo !== null">{{
                protocolInfo
              }}</b-alert>
              <b-card v-for="monitoring in monitorings" :key="monitoring._id">
                <p class="card-text">
                  <i class="fa fa-calendar-alt" aria-hidden="true" />
                  {{ monitoring.createdAt | datetime }}
                </p>
                <p
                  v-if="monitoring.type === DSARMonitoringType.COMPANY"
                  class="card-text"
                >
                  {{ monitoring.description }}
                </p>
                <p
                  v-else-if="monitoring.type === DSARMonitoringType.RESPONSE"
                  class="card-text text-info"
                >
                  <i class="fas fa-reply" aria-hidden="true" />
                  {{ monitoring.response.text }}
                </p>
              </b-card>
              <template v-if="response.enabled">
                <b-row>
                  <b-col>
                    <b-form-group
                      class="mb-0"
                      :label="$t('labels.insertNewInformation')"
                      :description="$t('labels.insertNewInformationDesc')"
                    >
                      <b-form-textarea
                        v-model="response.text"
                        v-validate="rules.response"
                        class="form-control"
                        aria-describedby="invalidInformation"
                        name="information"
                        :state="validateState('information')"
                      />
                      <b-form-invalid-feedback id="invalidInformation">
                        {{ errors.first("information") }}
                      </b-form-invalid-feedback>
                    </b-form-group>
                  </b-col>
                </b-row>
                <b-row>
                  <b-col>
                    <b-button
                      variant="link"
                      :disabled="loading"
                      class="float-right"
                      @click="sendResponse"
                    >
                      <i class="fas fa-reply" aria-hidden="true" />
                      {{ $t("labels.sendInformation") }}
                    </b-button>
                  </b-col>
                </b-row>
                <hr />
              </template>
            </template>
            <template v-if="type === ProtocolType.OMBUDSMAN">
              <b-alert :show="protocolInfo !== null">{{
                protocolInfo
              }}</b-alert>
              <b-card v-for="monitoring in monitorings" :key="monitoring._id">
                <p class="card-text">
                  <i class="fa fa-calendar-alt" aria-hidden="true" />
                  {{ monitoring.createdAt | datetime }}
                </p>
                <p
                  v-if="monitoring.type === OmbudsmanMonitoringType.COMPANY"
                  class="card-text"
                >
                  {{ monitoring.description }}
                </p>
                <p
                  v-else-if="
                    monitoring.type === OmbudsmanMonitoringType.RESPONSE
                  "
                  class="card-text text-info"
                >
                  <i class="fas fa-reply" aria-hidden="true" />
                  {{ monitoring.response.text }}
                </p>
              </b-card>
              <template v-if="response.enabled">
                <b-row>
                  <b-col>
                    <b-form-group
                      class="mb-0"
                      :label="$t('labels.insertNewInformation')"
                      :description="$t('labels.insertNewInformationDesc')"
                    >
                      <b-form-textarea
                        v-model="response.text"
                        v-validate="rules.response"
                        class="form-control"
                        aria-describedby="invalidInformation"
                        name="information"
                        :state="validateState('information')"
                      />
                      <b-form-invalid-feedback id="invalidInformation">
                        {{ errors.first("information") }}
                      </b-form-invalid-feedback>
                    </b-form-group>
                  </b-col>
                </b-row>
                <b-row>
                  <b-col>
                    <b-button
                      variant="link"
                      :disabled="loading"
                      class="float-right"
                      @click="sendResponse"
                    >
                      <i class="fas fa-reply" aria-hidden="true" />
                      {{ $t("labels.sendInformation") }}
                    </b-button>
                  </b-col>
                </b-row>
                <hr />
              </template>
            </template>
            <b-card v-if="type === ProtocolType.QAS">
              <p class="card-text">
                <strong>Dúvida/Sugestão</strong> (<small
                  ><i class="fa fa-calendar-alt" aria-hidden="true" />
                  {{ qas.createdAt | datetime }}</small
                >):<br />
                {{ qas.text }}
              </p>
              <p class="card-text">
                <strong>Resposta</strong> (<small
                  ><i class="fa fa-calendar-alt" aria-hidden="true" />
                  {{ qas.updatedAt | datetime }}</small
                >):<br />
                {{ qas.response }}
              </p>
            </b-card>
            <b-button
              :disabled="loading"
              class="action-button"
              @click="$router.push('/home')"
              ><i class="fas fa-home" aria-hidden="true" />
              {{ $t("labels.home") }}
            </b-button>
            <b-button
              :disabled="loading"
              class="action-button"
              variant="primary"
              @click="reset"
              ><i class="fas fa-search" aria-hidden="true" />
              {{ $t("labels.newSearch") }}
            </b-button>
          </b-card-body>
        </b-card>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { consultProtocol, sendProtocolResponse } from "@/api/main";
import ProtocolType from "../../../../../backend/models/ProtocolType.ts";
import MonitoringType from "../../../../../backend/models/MonitoringType.ts";
import DSARMonitoringType from "../../../../../backend/models/dsar/DSARMonitoringType.ts";
import OmbudsmanMonitoringType from "../../../../../backend/models/ombudsman/OmbudsmanMonitoringType.ts";

const Actions = {
  PROTOCOL: 1,
  RESPONSE: 2,
};
export default {
  name: "FollowProtocol",
  props: {
    protocol: {
      type: String,
      default: "",
      required: false,
    },
  },
  data() {
    return {
      ProtocolType,
      MonitoringType,
      DSARMonitoringType,
      OmbudsmanMonitoringType,
      hasResponse: false,
      action: Actions.PROTOCOL,
      type: null,
      protocolModel: this.protocol,
      qas: {},
      monitorings: [],
      createdAt: null,
      response: {
        text: "",
        enabled: false,
      },
      loading: false,
      protocolInfo: null,
      rules: {
        protocol: {
          required: true,
        },
        response: {
          required: true,
          min: 10,
        },
      },
    };
  },
  computed: {
    ...mapGetters(["portal"]),
  },
  mounted() {
    if (this.protocol) {
      this.execProtocolAction();
    }
  },
  methods: {
    validateState(ref) {
      if (
        this.vFields[ref] &&
        (this.vFields[ref].dirty || this.vFields[ref].validated)
      ) {
        return !this.errors.has(ref);
      }
      return null;
    },
    async executeAction() {
      if (this.action === Actions.PROTOCOL) {
        await this.execProtocolAction();
      } else if (this.action === Actions.RESPONSE) {
        await this.execResponseAction();
      }
    },

    async checkProtocol(evt) {
      evt.preventDefault();
      if (await this.$validator.validateAll()) {
        this.loading = true;
        this.action = Actions.PROTOCOL;
        await this.executeAction();
      }
      return false;
    },

    async sendResponse(evt) {
      evt.preventDefault();
      if (await this.$validator.validateAll()) {
        this.loading = true;
        this.action = Actions.RESPONSE;
        await this.executeAction();
      }
      return false;
    },

    handleResponse(payload) {
      this.type = payload.type;
      this.createdAt = payload.createdAt;
      this.protocolInfo = payload.info || null;
      if (payload.type === ProtocolType.QAS) {
        this.qas = payload.qas;
        this.hasResponse = true;
      } else if (
        [
          ProtocolType.COMPLAINT,
          ProtocolType.OMBUDSMAN,
          ProtocolType.DSAR,
        ].includes(payload.type)
      ) {
        this.monitorings = payload.monitoring || [];
        this.response.enabled = payload.responseEnabled;
        this.hasResponse = this.response.enabled || this.monitorings.length > 0;
      }
    },

    isNumber(evt) {
      const charCode = evt.which ? evt.which : evt.keyCode;
      if (charCode > 31 && (charCode < 48 || charCode > 57)) {
        evt.preventDefault();
      } else {
        return true;
      }
    },

    reset() {
      this.type = null;
      this.hasResponse = false;
      this.monitorings = [];
      this.qas = {};
      this.protocolModel = "";
      this.protocolInfo = null;
    },

    async execProtocolAction() {
      try {
        const response = await consultProtocol(
          this.portal.slug,
          this.protocolModel
        );
        const payload = response.data;
        if (payload.type) {
          this.handleResponse(payload);
        } else if (payload.info) {
          this.protocolInfo = payload.info;
        }
        this.loading = false;
      } catch (error) {
        this.loading = false;
        if (
          error.response.status === 403 ||
          error.response.status === 400 ||
          error.response.status === 404
        ) {
          this.$bvToast.toast(error.response.data, {
            title: "Erro",
            toastClass: "custom-toast custom-toast-error",
            variant: "light",
            solid: true,
            autoHideDelay: 10000,
            noCloseButton: false,
          });
        } else {
          this.$bvToast.toast(error.response.statusText, {
            title: "Erro",
            toastClass: "custom-toast custom-toast-error",
            variant: "light",
            solid: true,
            autoHideDelay: 10000,
            noCloseButton: false,
          });
        }
      }
    },

    async execResponseAction() {
      try {
        const response = await sendProtocolResponse(
          this.portal.slug,
          this.protocolModel,
          this.response.text
        );
        this.monitorings.unshift(response.data);
        this.$bvToast.toast(this.$t("followup.successResponse"), {
          title: "Sucesso",
          toastClass: "custom-toast custom-toast-success",
          variant: "light",
          solid: true,
          autoHideDelay: 5000,
          noCloseButton: false,
        });
        this.response.text = null;
        await this.$validator.reset();
        window.scrollTo(0, 0);
        this.loading = false;
      } catch (error) {
        this.loading = false;
        if (
          error.response.status === 403 ||
          error.response.status === 400 ||
          error.response.status === 404
        ) {
          this.$bvToast.toast(error.response.data, {
            title: "Erro",
            toastClass: "custom-toast custom-toast-error",
            variant: "light",
            solid: true,
            autoHideDelay: 10000,
            noCloseButton: false,
          });
        } else {
          this.$bvToast.toast(error.response.statusText, {
            title: "Erro",
            toastClass: "custom-toast custom-toast-error",
            variant: "light",
            solid: true,
            autoHideDelay: 10000,
            noCloseButton: false,
          });
        }
      }
    },
  },
};
</script>
<style lang="scss">
@media (max-width: 576px) {
  .action-button {
    width: 100%;

    & ~ .action-button {
      margin-top: 1rem;
    }
  }
}

@media (min-width: 576px) {
  .action-button ~ .action-button {
    margin-left: 1rem;
  }
}
</style>
